import type { Problema } from "./contrato";

/*
 * Toda resposta de erro da API é `application/problem+json` (RFC 9457). O painel
 * trata UM formato de erro, e não dois — foi por isso que a API administrativa
 * repetiu o formato da pública.
 *
 * Regra de leitura, do handoff §2:
 *   `codigo`  -> lógica
 *   `detail`  -> texto de tela
 *   `type`    -> identidade do erro (uma URL que responde 200)
 */

/** Erro de uma chamada à API, já com o problema interpretado quando havia um. */
export class FalhaDaApi extends Error {
  readonly status: number;
  readonly problema: Problema | null;
  /** O `Retry-After`, em segundos. `null` quando a resposta não trouxe. */
  readonly tenteEmSegundos: number | null;

  constructor(
    status: number,
    problema: Problema | null,
    mensagem: string,
    tenteEmSegundos: number | null = null,
  ) {
    super(mensagem);
    this.name = "FalhaDaApi";
    this.status = status;
    this.problema = problema;
    this.tenteEmSegundos = tenteEmSegundos;
  }

  get codigo(): string | null {
    return this.problema?.codigo ?? null;
  }

  get traceID(): string | null {
    return this.problema?.trace_id ?? null;
  }
}

/*
 * O texto que vai para a tela.
 *
 * `detail` primeiro porque a API o escreve para ser lido por gente e diz o que
 * fazer — "O convênio 756/12345 está bloqueado por credencial inválida.
 * Atualize a credencial para voltar a emitir." O `title` é o rótulo curto, e só
 * entra quando não há `detail`.
 *
 * A frase final NUNCA é "ocorreu um erro inesperado": ela diz o que a tela sabe
 * de fato, que é o código do protocolo (doc 06a §6).
 */
export function textoDoErro(erro: unknown): string {
  if (erro instanceof FalhaDaApi) {
    const problema = erro.problema;
    if (problema?.detail) return problema.detail;
    if (problema?.title) return problema.title;
    return `A API respondeu ${erro.status} sem descrever o motivo.`;
  }

  /*
   * Falha de transporte: o pedido não chegou a receber resposta. Dizer isso é
   * mais útil que "erro inesperado", porque aponta para onde procurar — rede,
   * proxy, serviço fora — em vez de para o painel.
   */
  if (erro instanceof TypeError) {
    return "Não foi possível falar com a API. Verifique a conexão e tente de novo.";
  }

  return erro instanceof Error && erro.message
    ? erro.message
    : "A operação não pôde ser concluída.";
}

/**
 * O identificador do caminho não leva a nada: `404`, ou `400` de identificador
 * malformado num link colado pela metade. Para quem abriu o link, os dois são o
 * mesmo fato, e a tela diz "não encontrado" com a saída para a lista.
 */
export function recursoInexistente(erro: unknown): boolean {
  return (
    erro instanceof FalhaDaApi && (erro.status === 404 || erro.codigo === "identificador_invalido")
  );
}

/** Um campo que a recusa aponta, na forma de `defeitos`. */
export interface CampoRecusado {
  /** O nome do corpo: `conta_dv`, ou `endereco.cep` no endereço aninhado. */
  campo: string;
  codigo: string;
  mensagem: string;
}

/**
 * Os campos que a recusa aponta, sem interpretar `detail` (design D14).
 *
 * `defeitos` vem primeiro, e `campo` só vale quando não há lista: a recusa de um
 * defeito só traz os dois, e contá-la duas vezes marcaria o campo com a frase
 * errada. A ordem é a do catálogo, e não a do formulário.
 *
 * Quem monta formulário usa `errosDoFormulario`, de `telas/cedentes/recusas.ts`,
 * que traduz o texto e não deixa campo fora do formulário sumir.
 */
export function camposRecusados(erro: unknown): CampoRecusado[] {
  if (!(erro instanceof FalhaDaApi) || erro.problema === null) return [];

  const { defeitos, campo, codigo } = erro.problema;
  if (defeitos !== undefined && defeitos.length > 0) {
    return defeitos.map((defeito) => ({ ...defeito }));
  }
  if (campo !== undefined && campo !== "") {
    return [{ campo, codigo, mensagem: textoDoErro(erro) }];
  }
  return [];
}

/**
 * O `Retry-After` em segundos. A API só escreve segundos: a forma de data do cabeçalho vira
 * `null`, e quem lê segue sem espera, em vez de esperar um número inventado.
 */
export function segundosDoRetryAfter(valor: string | null): number | null {
  return valor !== null && /^\d+$/.test(valor.trim()) ? Number(valor) : null;
}

/**
 * Interpreta o corpo de uma resposta de erro.
 *
 * Devolve `null` quando o corpo não é um problema RFC 9457 — o que acontece de
 * verdade: um 502 do Caddy é HTML, e um 413 pode vir sem corpo. Fingir que todo
 * erro tem `codigo` faria a lógica de tratamento ler `undefined` como se fosse
 * um código.
 */
export async function lerProblema(resposta: Response): Promise<Problema | null> {
  const tipo = resposta.headers.get("content-type") ?? "";
  if (!tipo.includes("json")) return null;

  try {
    const corpo: unknown = await resposta.json();
    if (
      typeof corpo === "object" &&
      corpo !== null &&
      "codigo" in corpo &&
      "status" in corpo
    ) {
      return corpo as Problema;
    }
    return null;
  } catch {
    return null;
  }
}
